/**
 * API Service for Kartal Air
 * Handles all backend API communications
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Generic fetch wrapper with error handling
 */
async function fetchAPI(endpoint, options = {}) {
  const url = `${API_BASE_URL}/api${endpoint}`;
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  const adminToken = localStorage.getItem('admin_token');
  if (adminToken) {
    defaultHeaders['Authorization'] = `Bearer ${adminToken}`;
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    
    // Handle 204 No Content
    if (response.status === 204) {
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
}

// ============ Build Logs API ============
export const buildLogsAPI = {
  getAll: () => fetchAPI('/build-logs'),
  getById: (id) => fetchAPI(`/build-logs/${id}`),
  create: (data) => fetchAPI('/build-logs', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => fetchAPI(`/build-logs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => fetchAPI(`/build-logs/${id}`, {
    method: 'DELETE',
  }),
};

// ============ Team API ============
export const teamAPI = {
  getAll: () => fetchAPI('/team'),
  getById: (id) => fetchAPI(`/team/${id}`),
  create: (data) => fetchAPI('/team', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => fetchAPI(`/team/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => fetchAPI(`/team/${id}`, {
    method: 'DELETE',
  }),
};

// ============ Sponsors API ============
export const sponsorsAPI = {
  getAll: () => fetchAPI('/sponsors'),
  getById: (id) => fetchAPI(`/sponsors/${id}`),
  create: (data) => fetchAPI('/sponsors', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => fetchAPI(`/sponsors/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => fetchAPI(`/sponsors/${id}`, {
    method: 'DELETE',
  }),
};

// ============ Contact API ============
export const contactAPI = {
  submit: (data) => fetchAPI('/contact', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getAll: () => fetchAPI('/contact'),
  getById: (id) => fetchAPI(`/contact/${id}`),
  markAsRead: (id) => fetchAPI(`/contact/${id}/read`, {
    method: 'PUT',
  }),
  delete: (id) => fetchAPI(`/contact/${id}`, {
    method: 'DELETE',
  }),
};

// ============ Documents API ============
export const documentsAPI = {
  getAll: () => fetchAPI('/documents'),
  getById: (id) => fetchAPI(`/documents/${id}`),
  getDownloadUrl: (id) => `${API_BASE_URL}/api/documents/${id}/download`,
  getProofOfFlight: () => fetchAPI('/documents/competition/proof-of-flight'),
  getTechnicalReport: () => fetchAPI('/documents/competition/technical-report'),
  upload: async (file, name, description = '', category = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('description', description);
    formData.append('category', category);
    
    const response = await fetch(`${API_BASE_URL}/api/documents`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Upload failed');
    }
    
    return await response.json();
  },
  update: (id, data) => fetchAPI(`/documents/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => fetchAPI(`/documents/${id}`, {
    method: 'DELETE',
  }),
};

// ============ Technical Specs API ============
export const specsAPI = {
  get: () => fetchAPI('/specs'),
};

// ============ Page Content API ============
export const contentAPI = {
  getPages: () => fetchAPI('/content'),
  getPageContent: (pageId) => fetchAPI(`/content/${pageId}`),
  updatePageContent: (pageId, content) => fetchAPI(`/content/${pageId}`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  }),
  uploadImage: async (file, pageId, contentKey) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('page_id', pageId);
    formData.append('content_key', contentKey);
    
    const response = await fetch(`${API_BASE_URL}/api/content/upload-image`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error('Failed to upload image');
    }
    
    return response.json();
  },
  resetPage: (pageId) => fetchAPI(`/content/reset/${pageId}`, { method: 'POST' }),
};

// ============ Gallery API ============
export const galleryAPI = {
  getAll: (category = null) => {
    const query = category ? `?category=${category}` : '';
    return fetchAPI(`/gallery${query}`);
  },
  getById: (id) => fetchAPI(`/gallery/${id}`),
  getImageUrl: (filename) => `${API_BASE_URL}/api/gallery/file/${filename}`,
};

// ============ Achievements API ============
export const achievementsAPI = {
  // Achievements
  getAll: (categoryId = null, featured = null) => {
    const params = new URLSearchParams();
    if (categoryId) params.append('category_id', categoryId);
    if (featured !== null) params.append('featured', featured);
    const query = params.toString() ? `?${params.toString()}` : '';
    return fetchAPI(`/achievements${query}`);
  },
  getFeatured: () => fetchAPI('/achievements/featured'),
  getById: (id) => fetchAPI(`/achievements/${id}`),
  create: (data) => fetchAPI('/achievements', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => fetchAPI(`/achievements/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => fetchAPI(`/achievements/${id}`, {
    method: 'DELETE',
  }),
  
  // Categories
  getCategories: () => fetchAPI('/achievements/categories'),
  getCategoryById: (id) => fetchAPI(`/achievements/categories/${id}`),
  createCategory: (data) => fetchAPI('/achievements/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateCategory: (id, data) => fetchAPI(`/achievements/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteCategory: (id) => fetchAPI(`/achievements/categories/${id}`, {
    method: 'DELETE',
  }),
};

// ============ YouTube Videos API ============
export const youtubeAPI = {
  getAll: () => fetchAPI('/youtube-videos'),
  getById: (id) => fetchAPI(`/youtube-videos/${id}`),
  update: (videos) => fetchAPI('/youtube-videos', {
    method: 'PUT',
    body: JSON.stringify(videos),
  }),
  create: (data) => fetchAPI('/youtube-videos', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  delete: (id) => fetchAPI(`/youtube-videos/${id}`, {
    method: 'DELETE',
  }),
};

// ============ Vehicle Gallery API ============
export const vehicleGalleryAPI = {
  getAll: (category = null) => {
    const query = category && category !== 'all' ? `?category=${category}` : '';
    return fetchAPI(`/vehicle-gallery${query}`);
  },
  getCategories: () => fetchAPI('/vehicle-categories'),
  getById: (id) => fetchAPI(`/vehicle-gallery/${id}`),
  create: (data) => fetchAPI('/vehicle-gallery', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => fetchAPI(`/vehicle-gallery/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  reorder: (id, direction) => fetchAPI(`/vehicle-gallery/${id}/reorder?direction=${direction}`, {
    method: 'PUT',
  }),
  delete: (id) => fetchAPI(`/vehicle-gallery/${id}`, {
    method: 'DELETE',
  }),
};

// ============ Vehicle Categories API ============
export const vehicleCategoriesAPI = {
  getAll: () => fetchAPI('/vehicle-categories'),
  getById: (id) => fetchAPI(`/vehicle-categories/${id}`),
  create: (data) => fetchAPI('/vehicle-categories', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => fetchAPI(`/vehicle-categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  reorder: (id, direction) => fetchAPI(`/vehicle-categories/${id}/reorder?direction=${direction}`, {
    method: 'PUT',
  }),
  delete: (id) => fetchAPI(`/vehicle-categories/${id}`, {
    method: 'DELETE',
  }),
};

export default {
  buildLogs: buildLogsAPI,
  team: teamAPI,
  sponsors: sponsorsAPI,
  contact: contactAPI,
  documents: documentsAPI,
  specs: specsAPI,
  content: contentAPI,
  gallery: galleryAPI,
  achievements: achievementsAPI,
  youtube: youtubeAPI,
  vehicleGallery: vehicleGalleryAPI,
  vehicleCategories: vehicleCategoriesAPI,
};
